import { apolloClient } from '@/common/service/apolloClient'

import {
  CmsMappingTypeEnum,
  CreateCartItemAttributes,
  QueryEstimateArgs
} from '@/types/graphqlQuery'
import { DocumentNode } from 'graphql'
import {
  addToCartMutation,
  getEstimateQuery,
  brandsQuery,
  cmsCategoryProductsQuery,
  industriesQuery,
  userDesignsQuery,
  deleteDesignMutation,
  getCMSLinksQuery,
  remainingFastShippingQuantityQuery
} from '@/common/queries'
import { BrandQueryResponse } from '@/common/types/brands'
import { CmsCategoryProductResponse } from '@/common/types/cmsCategoryProduct'
import { OperationVariables } from '@apollo/client/core/types'

export const executeMutation = async<T>(mutation: DocumentNode, input: T, context = {}) => {
  const response = await apolloClient.mutate({
    mutation: mutation,
    variables: {
      input: input
    },
    context
  }).catch(err => {
    throw err
  })
  if (!response || (response.errors && response.errors.length)) throw response.errors
  return response?.data
}

export const executeQuery = async <T>(query: DocumentNode, variables?: OperationVariables, noCache = false) => {
  const response = await apolloClient.query({
    query: query,
    variables,
    fetchPolicy: noCache ? 'no-cache' : 'cache-first'
  }).catch(err => {
    throw err
  })
  if (!response || (response.errors && response.errors.length)) throw response.errors
  return response.data
}

export const addProductToCart = (attributes: CreateCartItemAttributes) => {
  return executeMutation(addToCartMutation, { attributes })
}

export const getEstimateDetail = (variables: QueryEstimateArgs) => {
  return executeQuery(getEstimateQuery, variables)
}

export const getIndustriesList = () => {
  return executeQuery(industriesQuery)
}

export const getBrandsData = () => {
  return apolloClient
    .query<BrandQueryResponse>({
      query: brandsQuery,
      variables: {},
      fetchPolicy: 'no-cache'
    }).then(res => {
      const error = res.errors || res.error
      if (error) {
        return Promise.reject(error)
      }
      return Promise.resolve(res.data.brands)
    }).catch(err => {
      return Promise.reject(err)
    })
}

export const getCmsCategoryProducts = (
  id: string,
  offset: number,
  limit: number
) => {
  return apolloClient
    .query<CmsCategoryProductResponse>({
      query: cmsCategoryProductsQuery,
      variables: { id, offset, limit },
      fetchPolicy: 'no-cache'
    })
    .then(res => {
      const error = res.errors || res.error
      if (error) {
        return Promise.reject(error)
      }
      return Promise.resolve(res)
    })
    .catch(err => {
      return Promise.reject(err)
    })
}

export const getUserDesigns = () => {
  return executeQuery(userDesignsQuery)
}

export const deleteUserDesign = (uuid: string) => {
  return executeMutation(deleteDesignMutation, {
    uuid
  })
}

export const getCMSLinks = (mappableType: CmsMappingTypeEnum, mappableIds: string[]) => {
  return executeQuery(getCMSLinksQuery, { mappableType, mappableIds })
}

export const getRemainingFastShippingQuantity = async (psi: number | string) => {
  const data = await executeQuery(remainingFastShippingQuantityQuery, {psi}, true)
  return data?.fastShippingLimit?.remainingQuantity
}
